import React, {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button } from '@/components/ui/button'
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
  useComboboxFilter,
  useComboboxFilteredItems,
} from '@/components/ui/combobox'
import type { ComboboxSize } from '@/components/ui/combobox'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { useUIVersion } from '@/utils/UIVersionContext'
import { useVirtualizer } from '@tanstack/react-virtual'
import { FieldValidator } from 'formik'
import { XIcon } from 'lucide-react'

type PrimitiveValue = string | number

export type LabeledValue = {
  value: PrimitiveValue
  label?: ReactNode
  key?: React.Key
}

export type SelectValue = PrimitiveValue | LabeledValue | Array<PrimitiveValue | LabeledValue>

export type SelectFilterOption = {
  key?: string
  value?: PrimitiveValue
  children?: string
  label?: string
  disabled?: boolean
}

export type SelectChangeOption = {
  key: string
  value: PrimitiveValue
  label: ReactNode
  children: ReactNode
  disabled?: boolean
}

type ParsedOption = {
  key: string
  value: PrimitiveValue
  label: ReactNode
  text: string
  disabled?: boolean
  groupKey?: string
  groupLabel?: ReactNode
}

export type SelectOptionInput = {
  key?: React.Key
  value: PrimitiveValue
  label?: ReactNode
  text?: string
  disabled?: boolean
}

export type SelectOptionGroupInput = {
  key?: React.Key
  label: ReactNode
  options: SelectOptionInput[]
}

export type SelectMenuItem =
  | {
      type: 'action'
      key?: React.Key
      label: ReactNode
      onSelect(): void
      disabled?: boolean
    }
  | {
      type: 'label'
      key?: React.Key
      label: ReactNode
    }
  | {
      type: 'separator'
      key?: React.Key
    }

export interface OptionProps {
  value: unknown
  children?: ReactNode
  disabled?: boolean
}

export interface OptionGroupProps {
  label: ReactNode
  children?: ReactNode
}

export const Option: React.FC<OptionProps> = () => null
export const OptionGroup: React.FC<OptionGroupProps> = () => null

const isPrimitiveValue = (value: unknown): value is PrimitiveValue =>
  (typeof value === 'string' && value !== '') || typeof value === 'number'

const getOptionKey = (value: PrimitiveValue) =>
  `${typeof value === 'number' ? 'n' : 's'}:${String(value)}`

const getValueFromInput = (value: unknown): PrimitiveValue | undefined => {
  if (isPrimitiveValue(value)) return value

  if (value && typeof value === 'object' && 'value' in value) {
    const nextValue = (value as { value?: unknown }).value
    if (isPrimitiveValue(nextValue)) return nextValue
  }

  return undefined
}

const getLabelFromInput = (value: unknown): ReactNode | undefined => {
  if (value && typeof value === 'object' && 'label' in value) {
    return (value as { label?: ReactNode }).label
  }

  return undefined
}

const getFallbackParsedOption = (value: unknown): ParsedOption | undefined => {
  if (!value || typeof value !== 'object' || !('value' in value)) {
    return undefined
  }

  const nextValue = getValueFromInput(value)

  if (nextValue === undefined) {
    return undefined
  }

  const label = getLabelFromInput(value)

  if (label === undefined) {
    return undefined
  }

  return {
    key: getOptionKey(nextValue),
    value: nextValue,
    label,
    text: getText(label),
  }
}

const getText = (node: ReactNode): string => {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(getText).join(' ')
  if (React.isValidElement(node)) return getText(node.props.children)
  return ''
}

const normalizeOptions = (
  children: ReactNode,
  options?: Array<SelectOptionInput | SelectOptionGroupInput>
): ParsedOption[] => {
  const fromOptions: ParsedOption[] = []

  options?.forEach((option) => {
    if ('options' in option) {
      const groupKey =
        option.key != null ? String(option.key) : `group:${getText(option.label) || 'unnamed'}`

      option.options.forEach((groupOption) => {
        const label = groupOption.label ?? String(groupOption.value)
        fromOptions.push({
          key: getOptionKey(groupOption.value),
          value: groupOption.value,
          label,
          text: groupOption.text ?? getText(label),
          disabled: groupOption.disabled,
          groupKey,
          groupLabel: option.label,
        })
      })

      return
    }

    const label = option.label ?? String(option.value)
    fromOptions.push({
      key: getOptionKey(option.value),
      value: option.value,
      label,
      text: option.text ?? getText(label),
      disabled: option.disabled,
    })
  })

  if (children == null) {
    return fromOptions
  }

  const seen = new Set(fromOptions.map((option) => option.key))

  const walk = (nodes: ReactNode, parentGroup?: { key: string; label: ReactNode }) => {
    React.Children.forEach(nodes, (child) => {
      if (!React.isValidElement(child)) return

      if (child.type === OptionGroup) {
        const groupProps = child.props as OptionGroupProps
        const groupKey = `group:${getText(groupProps.label) || Math.random().toString(36)}`
        walk(groupProps.children, { key: groupKey, label: groupProps.label })
        return
      }

      if (child.type !== Option) {
        const unknownProps = child.props as Record<string, unknown>
        if (!('value' in unknownProps)) return
      }

      const props = child.props as OptionProps
      const value = getValueFromInput(props.value)

      if (value === undefined) return

      const key = getOptionKey(value)
      if (seen.has(key)) return

      seen.add(key)
      const label = props.children ?? String(value)
      fromOptions.push({
        key,
        value,
        label,
        text: getText(label),
        disabled: props.disabled,
        groupKey: parentGroup?.key,
        groupLabel: parentGroup?.label,
      })
    })
  }

  walk(children)

  return fromOptions
}

const asArray = (value: SelectValue | undefined): Array<PrimitiveValue | LabeledValue> => {
  if (value == null) return []
  return Array.isArray(value) ? value : [value]
}

const getFilterCandidate = (item: ParsedOption): SelectFilterOption => ({
  key: item.key,
  value: item.value,
  label: item.text,
  children: item.text,
  disabled: item.disabled,
})

const filterParsedOptions = (
  options: ParsedOption[],
  query: string,
  defaultFilter: (item: string, query: string) => boolean,
  filterOption?: (inputValue: string, option: SelectFilterOption) => boolean
) => {
  if (query === '') {
    return options
  }

  if (filterOption) {
    return options.filter((item) => filterOption(query, getFilterCandidate(item)))
  }

  return options.filter((item) => defaultFilter(item.text, query))
}

const limitVisibleValues = (
  values: PrimitiveValue[],
  selectedValues: PrimitiveValue[],
  limit: number
) => {
  if (values.length <= limit) {
    return values
  }

  const nextValues: PrimitiveValue[] = []
  const seen = new Set<string>()

  const addValue = (value: PrimitiveValue) => {
    const key = getOptionKey(value)

    if (seen.has(key)) return

    seen.add(key)
    nextValues.push(value)
  }

  selectedValues.forEach(addValue)

  for (const value of values) {
    addValue(value)

    if (nextValues.length >= limit) {
      break
    }
  }

  return nextValues
}

const loadingContent = (showLoading?: boolean, notFoundContent?: ReactNode) =>
  showLoading ? 'Loading...' : (notFoundContent ?? 'No data found')

const wrapperStyle = (style?: CSSProperties, fullWidth?: boolean): CSSProperties =>
  fullWidth ? { width: '100%', ...(style ?? {}) } : (style ?? {})

const renderMenuItems = (menuItems: SelectMenuItem[] | undefined) => {
  if (!menuItems?.length) {
    return null
  }

  return menuItems.map((item, index) => {
    const key = item.key ?? `${item.type}-${index}`

    switch (item.type) {
      case 'label':
        return (
          <div key={key} className='px-2 py-1.5 text-xs text-muted-foreground'>
            {item.label}
          </div>
        )
      case 'separator':
        return <ComboboxSeparator key={key} />
      case 'action':
        return (
          <button
            key={key}
            type='button'
            className='flex w-full items-center gap-2 rounded-sm py-1.5 pl-2 pr-2 text-left text-sm outline-hidden hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50'
            disabled={item.disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              if (!item.disabled) item.onSelect()
            }}
          >
            {item.label}
          </button>
        )
    }
  })
}

const buildMenuNode = (
  menuNode: React.ReactNode,
  menuItems: SelectMenuItem[] | undefined,
  dropdownRender?: (originNode: React.ReactNode) => React.ReactNode
) => {
  const hasMenuItems = Boolean(menuItems?.length)
  const hasTrailingSeparator = menuItems?.[menuItems.length - 1]?.type === 'separator'

  const nextNode = hasMenuItems ? (
    <>
      {renderMenuItems(menuItems)}
      {!hasTrailingSeparator && <ComboboxSeparator />}
      {menuNode}
    </>
  ) : (
    menuNode
  )

  return dropdownRender ? dropdownRender(nextNode) : nextNode
}

const SELECT_OPTION_ESTIMATED_HEIGHT = 34
const SELECT_OPTION_OVERSCAN = 8
const SELECT_EMPTY_QUERY_VISIBLE_LIMIT = 200
const SELECT_QUERY_VISIBLE_LIMIT = 500
const SELECT_EMPTY_QUERY_LOAD_MORE_STEP = 200
const SELECT_QUERY_LOAD_MORE_STEP = 500
const SELECT_LOAD_MORE_THRESHOLD = 24

interface VirtualizedOptionsMenuProps {
  showLoading?: boolean
  notFoundContent?: ReactNode
  highlightedIndex: number | null
  getOption: (value: PrimitiveValue) => ParsedOption
  getVisibleEntries: () => Array<
    | { type: 'group'; key: string; label: ReactNode }
    | { type: 'item'; key: string; option: ParsedOption }
  >
  hasMoreItems?: boolean
  onReachEnd?: () => void
}

const VirtualizedOptionsMenu: React.FC<VirtualizedOptionsMenuProps> = ({
  showLoading,
  notFoundContent,
  highlightedIndex,
  getVisibleEntries,
  hasMoreItems = false,
  onReachEnd,
}) => {
  const items = useComboboxFilteredItems<PrimitiveValue>()
  const entries = getVisibleEntries()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const lastLoadTriggerCountRef = useRef(0)

  const virtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) =>
      entries[index]?.type === 'group' ? (index > 0 ? 42 : 34) : SELECT_OPTION_ESTIMATED_HEIGHT,
    overscan: SELECT_OPTION_OVERSCAN,
    getItemKey: (index) => entries[index]?.key ?? index,
    measureElement: (element) =>
      element?.getBoundingClientRect().height ?? SELECT_OPTION_ESTIMATED_HEIGHT,
  })

  const virtualItems = virtualizer.getVirtualItems()

  useEffect(() => {
    if (highlightedIndex == null || highlightedIndex < 0 || highlightedIndex >= items.length) {
      return
    }

    let itemCounter = -1
    let targetEntryIndex = -1

    for (let i = 0; i < entries.length; i += 1) {
      if (entries[i]?.type !== 'item') continue
      itemCounter += 1
      if (itemCounter === highlightedIndex) {
        targetEntryIndex = i
        break
      }
    }

    if (targetEntryIndex >= 0) {
      virtualizer.scrollToIndex(targetEntryIndex, { align: 'auto' })
    }
  }, [highlightedIndex, entries, items.length, virtualizer])

  useEffect(() => {
    if (!hasMoreItems || !onReachEnd || virtualItems.length === 0) {
      if (!hasMoreItems) {
        lastLoadTriggerCountRef.current = 0
      }
      return
    }

    const lastVirtualItem = virtualItems[virtualItems.length - 1]

    if (
      lastVirtualItem.index >= entries.length - SELECT_LOAD_MORE_THRESHOLD &&
      lastLoadTriggerCountRef.current !== entries.length
    ) {
      lastLoadTriggerCountRef.current = entries.length
      onReachEnd()
    }
  }, [hasMoreItems, entries.length, onReachEnd, virtualItems])

  return (
    <>
      <ComboboxEmpty>{loadingContent(showLoading, notFoundContent)}</ComboboxEmpty>
      <ComboboxList ref={scrollRef}>
        {entries.length > 0 && (
          <div
            style={{
              height: virtualizer.getTotalSize(),
              position: 'relative',
              width: '100%',
            }}
          >
            {virtualItems.map((virtualItem) => {
              const entry = entries[virtualItem.index]
              if (!entry) return null

              return (
                <div
                  key={entry.key}
                  data-index={virtualItem.index}
                  ref={virtualizer.measureElement}
                  style={{
                    left: 0,
                    position: 'absolute',
                    top: 0,
                    transform: `translateY(${virtualItem.start}px)`,
                    width: '100%',
                  }}
                >
                  {entry.type === 'group' ? (
                    <ComboboxGroup className='pt-1'>
                      {virtualItem.index > 0 && <ComboboxSeparator className='mb-1 mt-0' />}
                      <ComboboxLabel className='block border-b border-border bg-muted/60 font-semibold text-foreground'>
                        {entry.label}
                      </ComboboxLabel>
                    </ComboboxGroup>
                  ) : (
                    <ComboboxItem
                      value={entry.option.value}
                      index={items.findIndex((item) => item === entry.option.value)}
                      disabled={entry.option.disabled}
                    >
                      {entry.option.label}
                    </ComboboxItem>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </ComboboxList>
    </>
  )
}

export interface SelectProps<ValueType = SelectValue> {
  id?: string
  readOnly?: boolean
  name?: string
  disabled?: boolean
  defaultValue?: SelectValue
  mode?: 'multiple' | 'tags' | 'combobox'
  error?: string
  value?: SelectValue
  onChange?: (value: ValueType, option?: SelectChangeOption | SelectChangeOption[]) => void
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  onBlur?: React.FocusEventHandler<HTMLElement>
  children?: React.ReactNode[] | React.ReactNode
  options?: Array<SelectOptionInput | SelectOptionGroupInput>
  showSearch?: boolean
  showDropdown?: boolean
  allowClear?: boolean
  filterOption?: (inputValue: string, option: SelectFilterOption) => boolean
  placeholder?: string
  style?: CSSProperties
  bordered?: boolean
  size?: ComboboxSize
  fullWidth?: boolean
  labelInValue?: boolean
  className?: string
  dropdownStyle?: CSSProperties
  dropdownRender?: (originNode: React.ReactNode) => React.ReactNode
  menuItems?: SelectMenuItem[]
  open?: boolean
  validate?: FieldValidator
  optionFilterProp?: string
  showLoading?: boolean
  notFoundContent?: React.ReactNode
  onSearch?: (q: string) => void
  'data-test'?: string
}

export interface MultiSelectProps<ValueType = SelectValue>
  extends Omit<SelectProps<ValueType>, 'mode'> {
  autoClearSearchValue?: boolean
  maxVisibleChips?: number
  selectionSummaryThreshold?: number
  renderSelectionSummary?: (selectedOptions: SelectChangeOption[]) => ReactNode
  mode?: 'multiple' | 'tags' | 'combobox'
}

const toChangeOption = (option: ParsedOption): SelectChangeOption => ({
  key: option.key,
  value: option.value,
  label: option.label,
  children: option.label,
  disabled: option.disabled,
})

const renderDefaultSelectionSummary = (selectedOptions: SelectChangeOption[]) =>
  `${selectedOptions.length} selected`

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SelectComponentType = React.FC<SelectProps<any>> & {
  Option: typeof Option
  OptionGroup: typeof OptionGroup
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MultiSelectComponentType = React.FC<MultiSelectProps<any>> & {
  Option: typeof Option
  OptionGroup: typeof OptionGroup
}

export const MultiSelect = (({
  children,
  options: optionsProp,
  fullWidth = false,
  showDropdown = true,
  showLoading = false,
  showSearch = false,
  labelInValue = false,
  autoClearSearchValue = true,
  className,
  size = 'default',
  readOnly,
  filterOption,
  maxVisibleChips,
  selectionSummaryThreshold,
  renderSelectionSummary,
  menuItems,
  ...props
}: MultiSelectProps<unknown>) => {
  const anchorRef = useComboboxAnchor()
  const options = useMemo(() => normalizeOptions(children, optionsProp), [children, optionsProp])
  const optionMap = useMemo(() => new Map(options.map((option) => [option.key, option])), [options])
  const itemValues = useMemo(() => options.map((option) => option.value), [options])
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)
  const [inputValue, setInputValue] = useState('')
  const { contains } = useComboboxFilter({ multiple: true })
  const isMutable = !readOnly && !props.disabled
  const { isNewUI } = useUIVersion()
  const isLargeSize = size === 'lg' || size === 'large'

  const controlled = props.value !== undefined
  const [internalValue, setInternalValue] = useState<PrimitiveValue[]>(
    asArray(props.defaultValue)
      .map(getValueFromInput)
      .filter((value): value is PrimitiveValue => value !== undefined)
  )

  const fallbackSelectedOptions = useMemo(() => {
    const rawValues = controlled ? asArray(props.value) : internalValue

    return new Map(
      rawValues
        .map(getFallbackParsedOption)
        .filter((option): option is ParsedOption => option !== undefined)
        .map((option) => [option.key, option])
    )
  }, [controlled, props.value, internalValue])

  const getResolvedOption = (value: PrimitiveValue): ParsedOption | undefined => {
    const key = getOptionKey(value)
    return optionMap.get(key) ?? fallbackSelectedOptions.get(key)
  }

  const getOption = (value: PrimitiveValue): ParsedOption => {
    return (
      getResolvedOption(value) ?? {
        key: getOptionKey(value),
        value,
        label: String(value),
        text: String(value),
      }
    )
  }

  const selectedValues = useMemo(
    () =>
      controlled
        ? asArray(props.value)
            .map(getValueFromInput)
            .filter((value): value is PrimitiveValue => value !== undefined)
        : internalValue,
    [controlled, props.value, internalValue]
  )
  const comboboxKey = readOnly
    ? `readonly:${selectedValues.map(getOptionKey).join('|')}`
    : 'editable'

  const selectedOptionValues = useMemo(
    () =>
      selectedValues
        .map((value) => getResolvedOption(value))
        .filter((option): option is ParsedOption => option !== undefined)
        .map((option) => option.value),
    [selectedValues, optionMap, fallbackSelectedOptions]
  )
  const selectedChangeOptions = useMemo(
    () => selectedValues.map(getOption).map(toChangeOption),
    [selectedValues, optionMap, fallbackSelectedOptions]
  )

  const isOpenControlled = props.open !== undefined
  const [internalOpen, setInternalOpen] = useState(false)
  const isOpen = showDropdown ? (isOpenControlled ? !!props.open : internalOpen) : false
  const [visibleItemLimit, setVisibleItemLimit] = useState(SELECT_EMPTY_QUERY_VISIBLE_LIMIT)
  const filterQuery = showSearch ? inputValue.trim() : ''
  const filteredItemValues = useMemo(
    () =>
      filterQuery === ''
        ? itemValues
        : filterParsedOptions(options, filterQuery, contains, filterOption).map(
            (option) => option.value
          ),
    [itemValues, options, filterQuery, contains, filterOption]
  )

  const visibleBatchSize =
    filterQuery === '' ? SELECT_EMPTY_QUERY_VISIBLE_LIMIT : SELECT_QUERY_VISIBLE_LIMIT
  const loadMoreStep =
    filterQuery === '' ? SELECT_EMPTY_QUERY_LOAD_MORE_STEP : SELECT_QUERY_LOAD_MORE_STEP

  useEffect(() => {
    setVisibleItemLimit(visibleBatchSize)
  }, [filterQuery, showSearch, visibleBatchSize])

  const visibleItemValues = useMemo(() => {
    if (!showSearch) return filteredItemValues

    return limitVisibleValues(filteredItemValues, selectedOptionValues, visibleItemLimit)
  }, [showSearch, filteredItemValues, selectedOptionValues, visibleItemLimit])

  const visibleOptions = useMemo(
    () =>
      visibleItemValues
        .map((value) => getResolvedOption(value))
        .filter((option): option is ParsedOption => option !== undefined),
    [visibleItemValues, optionMap, fallbackSelectedOptions]
  )

  const visibleEntries = useMemo(() => {
    const entries: Array<
      | { type: 'group'; key: string; label: ReactNode }
      | { type: 'item'; key: string; option: ParsedOption }
    > = []

    let currentGroupKey: string | undefined

    visibleOptions.forEach((option) => {
      if (option.groupKey && option.groupKey !== currentGroupKey) {
        currentGroupKey = option.groupKey
        entries.push({
          type: 'group',
          key: `group-header:${option.groupKey}`,
          label: option.groupLabel ?? '',
        })
      }

      if (!option.groupKey) {
        currentGroupKey = undefined
      }

      entries.push({
        type: 'item',
        key: option.key,
        option,
      })
    })

    return entries
  }, [visibleOptions])

  const hasMoreItems = showSearch && visibleItemValues.length < filteredItemValues.length
  const handleReachEnd = useCallback(() => {
    setVisibleItemLimit((currentLimit) => currentLimit + loadMoreStep)
  }, [loadMoreStep])

  const menuNode = (
    <VirtualizedOptionsMenu
      showLoading={showLoading}
      notFoundContent={props.notFoundContent}
      highlightedIndex={highlightedIndex}
      getOption={getOption}
      getVisibleEntries={() => visibleEntries}
      hasMoreItems={hasMoreItems}
      onReachEnd={handleReachEnd}
    />
  )

  const applySelectedValues = (nextValues: PrimitiveValue[]) => {
    const nextOptions = nextValues.map(getOption)

    if (!controlled) {
      setInternalValue(nextValues)
    }

    if (autoClearSearchValue) {
      setInputValue('')
    }

    if (!props.onChange) return

    if (labelInValue) {
      props.onChange(
        nextOptions.map((option) => ({
          value: option.value,
          label: option.label,
        })),
        nextOptions.map(toChangeOption)
      )
      return
    }

    props.onChange(nextValues, nextOptions.map(toChangeOption))
  }

  const clearSelection = () => {
    if (!isMutable || selectedValues.length === 0) return

    applySelectedValues([])
    setInputValue('')
    props.onSearch?.('')
  }

  const showClearButton = props.allowClear === true && isMutable && selectedValues.length > 0
  const resolvedSelectionSummaryThreshold =
    selectionSummaryThreshold ?? (renderSelectionSummary != null ? 0 : undefined)
  const resolvedRenderSelectionSummary = renderSelectionSummary ?? renderDefaultSelectionSummary
  const shouldRenderSelectionSummary =
    resolvedSelectionSummaryThreshold != null &&
    selectedValues.length > resolvedSelectionSummaryThreshold

  const handleAnchorMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!showDropdown || !isMutable) return

    const target = event.target as HTMLElement
    if (target.closest('button') || target.closest('input')) return

    if (!isOpenControlled) {
      setInternalOpen(true)
    }

    requestAnimationFrame(() => {
      anchorRef.current?.querySelector('input')?.focus()
    })
  }

  return (
    <div className={className} style={wrapperStyle(props.style, fullWidth)}>
      <Combobox<PrimitiveValue, true>
        key={comboboxKey}
        multiple
        autoHighlight
        id={props.id}
        name={props.name}
        data-test={props['data-test']}
        disabled={props.disabled}
        readOnly={readOnly}
        open={isOpen}
        virtualized
        items={visibleItemValues}
        filteredItems={visibleItemValues}
        value={selectedOptionValues}
        filter={null}
        inputValue={inputValue}
        itemToStringLabel={(item) => getOption(item).text}
        onOpenChange={(nextOpen, eventDetails) => {
          if (!nextOpen && eventDetails?.reason === 'item-press') {
            return
          }

          if (!nextOpen) {
            setHighlightedIndex(null)
            setInputValue('')
            setVisibleItemLimit(visibleBatchSize)
          }

          if (!isOpenControlled) {
            setInternalOpen(nextOpen)
          }
        }}
        onItemHighlighted={(_, eventDetails) => {
          setHighlightedIndex(eventDetails.index >= 0 ? eventDetails.index : null)
        }}
        onInputValueChange={(query) => {
          setInputValue(query)
          props.onSearch?.(query)
        }}
        onValueChange={(nextValue) => {
          if (!isMutable) return

          applySelectedValues((nextValue ?? []) as PrimitiveValue[])
        }}
      >
        <div
          ref={anchorRef}
          data-slot='multi-select-trigger'
          data-disabled={props.disabled ? 'true' : undefined}
          data-readonly={readOnly ? 'true' : undefined}
          className={cn(
            'flex min-w-0 items-stretch border border-input bg-transparent transition-[color,border-color]',
            isLargeSize ? 'min-h-9' : 'min-h-7',
            readOnly &&
              !props.disabled &&
              'cursor-default border-transparent border-b-input/40 rounded-none bg-transparent shadow-none dark:bg-transparent',
            props.disabled && 'cursor-not-allowed',
            isNewUI && 'rounded-md',
            !isNewUI && 'rounded-xs'
          )}
          onMouseDown={handleAnchorMouseDown}
        >
          <ComboboxChips
            size={size}
            disabled={props.disabled}
            readOnly={readOnly}
            className='min-h-0! min-w-0 flex-1 rounded-none border-0 bg-transparent shadow-none'
          >
            <ComboboxValue>
              {(values: PrimitiveValue[]) => (
                <>
                  {shouldRenderSelectionSummary ? (
                    <ComboboxChip showRemove={false} onClick={(event) => event.stopPropagation()}>
                      {resolvedRenderSelectionSummary(selectedChangeOptions)}
                    </ComboboxChip>
                  ) : (
                    <>
                      {values
                        .slice(0, maxVisibleChips == null ? values.length : maxVisibleChips)
                        .map((value) => {
                          const option = getOption(value)

                          return (
                            <ComboboxChip
                              key={option.key}
                              showRemove={props.allowClear !== false && isMutable}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {option.label}
                            </ComboboxChip>
                          )
                        })}

                      {maxVisibleChips != null && values.length > maxVisibleChips && (
                        <ComboboxChip
                          showRemove={false}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {`+${values.length - maxVisibleChips}`}
                        </ComboboxChip>
                      )}
                    </>
                  )}

                  <ComboboxChipsInput
                    disabled={props.disabled}
                    className={
                      showSearch || isOpen || values.length === 0
                        ? 'min-w-10 flex-1'
                        : 'w-0 min-w-0 flex-none overflow-hidden p-0 opacity-0'
                    }
                    placeholder={values.length === 0 ? props.placeholder : undefined}
                    readOnly={readOnly || !showSearch}
                    onFocus={props.onFocus}
                    onBlur={props.onBlur}
                  />
                </>
              )}
            </ComboboxValue>
          </ComboboxChips>

          {showLoading && (
            <Button size='icon-xs' variant='ghost'>
              <Spinner />
            </Button>
          )}
          {(showClearButton || showDropdown || showLoading) && !showLoading && (
            <div className='flex shrink-0 items-center gap-1 pr-2 has-[>button]:-mr-1'>
              {showClearButton && (
                <Button
                  size='icon-xs'
                  variant='ghost'
                  type='button'
                  aria-label='Clear selected options'
                  onMouseDown={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    clearSelection()
                  }}
                >
                  <XIcon className='size-4' />
                </Button>
              )}

              {!showClearButton && !showLoading && showDropdown && (
                <ComboboxTrigger
                  disabled={props.disabled}
                  className='flex size-6 items-center justify-center rounded-sm p-0 text-muted-foreground hover:text-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50'
                />
              )}
            </div>
          )}
        </div>

        {(showDropdown || props.dropdownRender) && isOpen && (
          <ComboboxContent style={props.dropdownStyle} anchor={anchorRef}>
            {buildMenuNode(menuNode, menuItems, props.dropdownRender)}
          </ComboboxContent>
        )}
      </Combobox>
    </div>
  )
}) as MultiSelectComponentType

export const Select = (({
  children,
  options: optionsProp,
  fullWidth = false,
  showDropdown = true,
  showSearch = false,
  showLoading = false,
  labelInValue = false,
  className,
  filterOption,
  readOnly,
  menuItems,
  mode,
  size = 'default',
  ...props
}: SelectProps<unknown>) => {
  if (mode === 'multiple') {
    return (
      <MultiSelect
        {...props}
        size={size}
        readOnly={readOnly}
        fullWidth={fullWidth}
        showDropdown={showDropdown}
        showLoading={showLoading}
        labelInValue={labelInValue}
        className={className}
        options={optionsProp}
      >
        {children}
      </MultiSelect>
    )
  }

  const anchorRef = useComboboxAnchor()
  const options = useMemo(() => normalizeOptions(children, optionsProp), [children, optionsProp])
  const optionMap = useMemo(() => new Map(options.map((option) => [option.key, option])), [options])
  const itemValues = useMemo(() => options.map((option) => option.value), [options])
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)

  const controlled = props.value !== undefined
  const [internalValue, setInternalValue] = useState<PrimitiveValue | undefined>(() => {
    const value = asArray(props.defaultValue)[0]
    return getValueFromInput(value)
  })

  const selectedRawValue = controlled ? asArray(props.value)[0] : internalValue
  const selectedValue = getValueFromInput(selectedRawValue)
  const comboboxKey = readOnly
    ? `readonly:${selectedValue === undefined ? 'empty' : getOptionKey(selectedValue)}`
    : 'editable'
  const fallbackSelectedOption = useMemo(
    () => getFallbackParsedOption(selectedRawValue),
    [selectedRawValue]
  )

  const getResolvedOption = (value: PrimitiveValue): ParsedOption | undefined => {
    const key = getOptionKey(value)
    return (
      optionMap.get(key) ??
      (fallbackSelectedOption?.key === key ? fallbackSelectedOption : undefined)
    )
  }

  const getOption = (value: PrimitiveValue): ParsedOption => {
    return (
      getResolvedOption(value) ?? {
        key: getOptionKey(value),
        value,
        label: String(value),
        text: String(value),
      }
    )
  }

  const selectedOption = selectedValue === undefined ? undefined : getResolvedOption(selectedValue)
  const selectedText = selectedOption?.text ?? ''

  const isOpenControlled = props.open !== undefined
  const [internalOpen, setInternalOpen] = useState(false)
  const isOpen = showDropdown ? (isOpenControlled ? !!props.open : internalOpen) : false
  const [inputValue, setInputValue] = useState(selectedText)
  const [queryChangedAfterOpen, setQueryChangedAfterOpen] = useState(false)
  const [visibleItemLimit, setVisibleItemLimit] = useState(SELECT_EMPTY_QUERY_VISIBLE_LIMIT)
  const { contains } = useComboboxFilter({ value: selectedValue })
  const isMutable = !readOnly && !props.disabled

  useEffect(() => {
    if (!isOpen) {
      setInputValue(selectedText)
      setQueryChangedAfterOpen(false)
    }
  }, [isOpen, selectedText])

  const notifyChange = (value: PrimitiveValue | null) => {
    if (!props.onChange) return

    const option = value == null ? null : getOption(value)

    if (labelInValue) {
      props.onChange(
        option
          ? {
              value: option.value,
              label: option.label,
            }
          : undefined,
        option ? toChangeOption(option) : undefined
      )
      return
    }

    props.onChange(option?.value, option ? toChangeOption(option) : undefined)
  }

  const filterQuery = showSearch && queryChangedAfterOpen ? inputValue.trim() : ''
  const filteredItemValues = useMemo(
    () =>
      filterQuery === ''
        ? itemValues
        : filterParsedOptions(options, filterQuery, contains, filterOption).map(
            (option) => option.value
          ),
    [itemValues, options, filterQuery, contains, filterOption]
  )

  const visibleBatchSize =
    filterQuery === '' ? SELECT_EMPTY_QUERY_VISIBLE_LIMIT : SELECT_QUERY_VISIBLE_LIMIT
  const loadMoreStep =
    filterQuery === '' ? SELECT_EMPTY_QUERY_LOAD_MORE_STEP : SELECT_QUERY_LOAD_MORE_STEP

  useEffect(() => {
    setVisibleItemLimit(visibleBatchSize)
  }, [filterQuery, showSearch, visibleBatchSize])

  const visibleItemValues = useMemo(() => {
    if (!showSearch) {
      return filteredItemValues
    }

    return limitVisibleValues(
      filteredItemValues,
      selectedOption ? [selectedOption.value] : [],
      visibleItemLimit
    )
  }, [showSearch, filteredItemValues, selectedOption, visibleItemLimit])

  const visibleOptions = useMemo(
    () =>
      visibleItemValues
        .map((value) => getResolvedOption(value))
        .filter((option): option is ParsedOption => option !== undefined),
    [visibleItemValues, optionMap, fallbackSelectedOption]
  )

  const visibleEntries = useMemo(() => {
    const entries: Array<
      | { type: 'group'; key: string; label: ReactNode }
      | { type: 'item'; key: string; option: ParsedOption }
    > = []

    let currentGroupKey: string | undefined

    visibleOptions.forEach((option) => {
      if (option.groupKey && option.groupKey !== currentGroupKey) {
        currentGroupKey = option.groupKey
        entries.push({
          type: 'group',
          key: `group-header:${option.groupKey}`,
          label: option.groupLabel ?? '',
        })
      }

      if (!option.groupKey) {
        currentGroupKey = undefined
      }

      entries.push({
        type: 'item',
        key: option.key,
        option,
      })
    })

    return entries
  }, [visibleOptions])

  const hasMoreItems = showSearch && visibleItemValues.length < filteredItemValues.length
  const handleReachEnd = useCallback(() => {
    setVisibleItemLimit((currentLimit) => currentLimit + loadMoreStep)
  }, [loadMoreStep])

  const menuNode = (
    <VirtualizedOptionsMenu
      showLoading={showLoading}
      notFoundContent={props.notFoundContent}
      highlightedIndex={highlightedIndex}
      getOption={getOption}
      getVisibleEntries={() => visibleEntries}
      hasMoreItems={hasMoreItems}
      onReachEnd={handleReachEnd}
    />
  )

  return (
    <Combobox<PrimitiveValue>
      key={comboboxKey}
      autoHighlight
      id={props.id}
      name={props.name}
      data-test={props['data-test']}
      disabled={props.disabled}
      readOnly={readOnly}
      open={isOpen}
      virtualized
      inputValue={inputValue}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setHighlightedIndex(null)
          setInputValue(selectedText)
          setQueryChangedAfterOpen(false)
          setVisibleItemLimit(visibleBatchSize)
        } else {
          setInputValue(showSearch ? '' : selectedText)
          setQueryChangedAfterOpen(false)
          setVisibleItemLimit(visibleBatchSize)
        }

        if (!isOpenControlled) {
          setInternalOpen(nextOpen)
        }
      }}
      items={visibleItemValues}
      filteredItems={visibleItemValues}
      value={selectedOption?.value ?? null}
      filter={null}
      itemToStringLabel={(item) => getOption(item).text}
      onItemHighlighted={(_, eventDetails) => {
        setHighlightedIndex(eventDetails.index >= 0 ? eventDetails.index : null)
      }}
      onInputValueChange={(query) => {
        setInputValue(query)
        setQueryChangedAfterOpen(true)
        props.onSearch?.(query)
      }}
      onValueChange={(nextValue) => {
        if (!isMutable) return

        const value = (nextValue as PrimitiveValue | null) ?? null

        if (!controlled) {
          setInternalValue(value ?? undefined)
        }

        setInputValue(value == null ? '' : getOption(value).text)
        setQueryChangedAfterOpen(false)
        notifyChange(value)
      }}
    >
      <ComboboxInput
        inputGroupRef={anchorRef}
        disabled={props.disabled}
        placeholder={props.placeholder}
        size={size}
        showTrigger={showDropdown}
        showClear={props.allowClear === true && isMutable}
        readOnly={readOnly}
        inputReadOnly={readOnly || !showSearch}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        className={className}
      />

      {(showDropdown || props.dropdownRender) && isOpen && (
        <ComboboxContent style={props.dropdownStyle} anchor={anchorRef}>
          {buildMenuNode(menuNode, menuItems, props.dropdownRender)}
        </ComboboxContent>
      )}
    </Combobox>
  )
}) as SelectComponentType

Select.Option = Option
Select.OptionGroup = OptionGroup
MultiSelect.Option = Option
MultiSelect.OptionGroup = OptionGroup
